import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../../components/card/card';
import { useFetch } from "../../components/hooks/useFetch";
import config from '../../config/config';
import { Loader } from "../../components/loader/loader";
import { Modal } from '../../components/modal/modal';
import Icon from '../../components/icon/icon';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function Data() {
  const [open, setOpen] = useState(false);
  const { data, loading, error } = useFetch(config.api.url);
  const [dataSet, setDataSet] = useState([]);
  const [filter, setFilter] = useState({});

  const headers = [
    { label: 'CO (ppm)', span: 'col-span-1' },
    { label: 'NO2 (ppm)', span: 'col-span-1' },
    { label: 'NO (k)', span: 'col-span-1' },
    { label: 'NOx (ppm)', span: 'col-span-1' },
    { label: 'Fecha', span: 'col-span-1' },
    { label: 'Hora', span: 'col-span-1' },
    { label: 'Ubicación', span: 'col-span-2' },
    { label: 'Origen', span: 'col-span-2' },
  ];

  const filters = config.filters;

  const onChangeFilter = (e, v) => {
    v === 'location'
      ? setFilter({ ...filter, label: e.label, location: e.key, hour: e.value })
      : setFilter({ ...filter, date: e });
  };

  const onFilter = () => {
    let filteredData = [...(data || [])];
    if (filter.date && filter.date.startDate && filter.date.endDate) {
      const [sy, sm, sd] = filter.date.startDate.split('-').map(Number);
      const [ey, em, ed] = filter.date.endDate.split('-').map(Number);
      const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
      const end = new Date(ey, em - 1, ed, 23, 59, 59, 999);
      filteredData = filteredData.filter((item) => {
        const fecha = new Date(item.date);
        return fecha >= start && fecha <= end;
      });
    }
    if (filter.location && filter.label) {
      const locationItem = filters[0].items.find((item) => filter.label.includes(item.label));
      const dateComplete = locationItem
        ? locationItem.dates[locationItem.values.indexOf(filter.hour)]
        : null;

      filteredData = filteredData.filter(({ location, date }) => {
        return (
          filter.label.includes(location) &&
          (!dateComplete || date.includes(dateComplete))
        );
      });
    }
    setDataSet(filteredData);
    setOpen(false);
  };

  const resetFilter = () => {
    setFilter({});
    setDataSet(data || []);
    setOpen(false);
  };

  useEffect(() => {
    if (data && Array.isArray(data)) {
      setDataSet(data);
    }
  }, [data]);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <Loader loading={loading} error={error}>
        {/* Header toolbar */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200/60 pb-4"
        >
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
              Registro Histórico de Mediciones
            </h1>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              Dataset crudo de concentraciones capturadas por la red de sensores IoT
            </p>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-600 bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-xl shadow-xs">
              {dataSet.length} Registros
            </span>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white font-bold text-xs shadow-md shadow-primary/20 hover:bg-primary-hover transition-all cursor-pointer"
            >
              <Icon className="text-xs">filter</Icon>
              <span>Filtrar Datos</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Filter Modal */}
        <Modal open={open} setOpen={setOpen}>
          <div className="bg-white rounded-2xl p-6 shadow-2xl border border-slate-100">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-primary-light text-primary flex items-center justify-center">
                  <Icon className="text-sm">filter</Icon>
                </div>
                <h3 className="text-lg font-bold text-slate-900">Filtrar Registros</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <Icon className="text-sm">xmark</Icon>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
              {filters.map(({ Component, label, variable, ...props }, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                    {label}
                  </label>
                  <Component
                    {...props}
                    change={(e) => onChangeFilter(e, variable)}
                    defaultValue={
                      variable === 'location'
                        ? filter.location
                        : (filter.date ? filter.date.dateHard : null)
                    }
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={resetFilter}
                className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs hover:bg-slate-50 transition-colors"
              >
                Restablecer
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2.5 rounded-xl text-slate-500 font-semibold text-xs hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={onFilter}
                className="px-5 py-2.5 rounded-xl bg-primary text-white font-bold text-xs shadow-md shadow-primary/25 hover:bg-primary-hover transition-all"
              >
                Aplicar Filtro
              </button>
            </div>
          </div>
        </Modal>

        {/* Data Table Card */}
        <motion.div variants={itemVariants}>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto max-h-[680px] overflow-y-auto">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-xs border-b border-slate-200 shadow-xs">
                  <tr>
                    {headers.map((h, i) => (
                      <th
                        key={i}
                        className={`px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-500 ${
                          i < 4 ? 'text-right' : ''
                        }`}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {dataSet.length > 0 ? (
                    dataSet.map((element, index) => {
                      const timeStr = element.date ? element.date.split('T')[1]?.substring(0, 5) : '--:--';
                      const dateStr = element.date ? new Date(element.date).toLocaleDateString() : 'N/A';

                      return (
                        <tr key={index} className="hover:bg-slate-50/70 transition-colors">
                          {/* CO */}
                          <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">
                            <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-700">
                              {element.co}
                            </span>
                          </td>
                          {/* NO2 */}
                          <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">
                            <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700">
                              {element.no2}
                            </span>
                          </td>
                          {/* NO */}
                          <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">
                            <span className="px-2 py-0.5 rounded-md bg-sky-50 text-sky-700">
                              {element.no}
                            </span>
                          </td>
                          {/* NOX */}
                          <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">
                            <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700">
                              {element.nox}
                            </span>
                          </td>
                          {/* Date */}
                          <td className="px-4 py-3 font-semibold text-slate-600">
                            {dateStr}
                          </td>
                          {/* Hour */}
                          <td className="px-4 py-3 font-mono font-medium text-slate-500">
                            {timeStr}
                          </td>
                          {/* Location */}
                          <td className="px-4 py-3">
                            <span className="font-semibold text-slate-800 block truncate max-w-[220px]">
                              {element.location}
                            </span>
                            {element.zone && (
                              <span className="text-[10px] text-slate-400 font-medium">
                                Zona: {element.zone}
                              </span>
                            )}
                          </td>
                          {/* Data Origin */}
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              {element.data_origin || 'Sensor IoT'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="py-16 text-center">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
                          <Icon className="text-xl">database</Icon>
                        </div>
                        <h3 className="text-sm font-bold text-slate-700 mb-1">
                          No se encontraron registros
                        </h3>
                        <p className="text-xs text-slate-400">
                          Intenta ajustar o reiniciar los filtros de búsqueda.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </motion.div>
      </Loader>
    </motion.div>
  );
}

